/**
 * Query-key factory for the portal's TanStack Query cache.
 *
 * Collection keys share a flat string prefix (e.g. "datasets") so callers can
 * refresh every per-project variant with a single prefix invalidation
 * (`invalidateQueries({ queryKey: ["datasets"] })`), while individual
 * entities use `[prefix, id]` keys under the same prefix.
 */
import type { components } from "@scalty/contracts";

type TrainRequest = components["schemas"]["TrainRequest"];

export const qk = {
  // Org-scoped project list. `includeArchived` re-keys to the archived-inclusive
  // variant (the organization/projects management page); the default (active
  // only) is what the project switcher owns. Mirrors qk.models(...): a bare
  // qk.projects() invalidation partial-matches every variant.
  projects: (options: { includeArchived?: boolean } = {}) =>
    ["projects", options] as const,
  datasets: (projectId: string | null) => ["datasets", { project: projectId }] as const,
  // What each kind of dataset accepts. Organization-independent and changed only
  // by a platform release, so it carries no variant.
  datasetKinds: () => ["dataset-kinds"] as const,
  dataset: (id: string) => ["datasets", id] as const,
  // One key per page shape. `limit` is part of the key because it is part of
  // what comes back: a picker that needs the whole project and a list that takes
  // the API default of 50 are two different results, and sharing one cache entry
  // would let whichever query ran last decide what the other renders.
  models: (
    projectId: string | null,
    options: { datasetId?: string; includeArchived?: boolean; limit?: number } = {},
  ) => ["models", { project: projectId, ...options }] as const,
  model: (id: string) => ["models", id] as const,
  // Durable exports of a model version (GET /models/{modelId}/exports). Nested
  // under the "models" prefix so one
  // ["models"] invalidation refreshes a model and everything derived from it.
  modelExports: (modelId: string) => ["models", modelId, "exports"] as const,
  heldOutEvaluation: (modelId: string) => ["models", modelId, "held-out-evaluation"] as const,
  // The environment's base-model catalog for text_generation training
  // (GET /models/catalog). Env-global, not project-scoped — one flat key.
  modelCatalog: () => ["model-catalog"] as const,

  // The settings registry: every platform default that shapes the examples a
  // training run writes and the model it trains, with its provenance (GET /settings).
  // Env-global policy, not org data — one flat key.
  platformSettings: () => ["platform-settings"] as const,
  // Environment-wide machine capability list. The count changes which
  // same-class/count historical median the endpoint returns; the dataset
  // and the recipe (epochs, sequence window) change the projection each
  // machine's estimate carries, so all four key it — an edited epoch count must
  // not be answered from the previous recipe's cache.
  accelerators: (
    acceleratorCount: number,
    datasetId = "",
    epochs: number | null = null,
    maxSeqLen: number | null = null,
  ) => ["accelerators", { count: acceleratorCount, datasetId, epochs, maxSeqLen }] as const,
  // Dry-run training projection (POST /models/estimate — free and idempotent,
  // so a POST-backed useQuery is sound). Keyed by the full TrainRequest body:
  // every input combination caches separately and any edit re-keys the query,
  // while the flat "training-estimate" prefix still invalidates them all.
  // `null` (an incomplete train form) backs the disabled/idle query.
  trainingEstimate: (request: TrainRequest | null) => ["training-estimate", request] as const,
  // Deployments are the first-class serving resource (RU-2): a named, slug-owning
  // record whose revisions form its append-only timeline. Collection keyed by
  // project; the entity + its revisions live under the same "deployments" prefix
  // so one prefix invalidation refreshes the list, a detail, and its timeline.
  deployments: (projectId: string | null) =>
    ["deployments", { project: projectId }] as const,
  deployment: (slug: string) => ["deployments", slug] as const,
  revisions: (slug: string) => ["deployments", slug, "revisions"] as const,
  // Knowledge bases are the grounding resource (GROUND-1): named document
  // collections a deployment's answers retrieve from. Collection keyed by
  // project; the entity and its documents key under the same "knowledgeBases"
  // prefix — like a deployment and its revisions — so one prefix
  // invalidation refreshes the list, a detail, and its documents together.
  knowledgeBases: (projectId: string | null) =>
    ["knowledgeBases", { project: projectId }] as const,
  knowledgeBase: (id: string) => ["knowledgeBases", id] as const,
  // The Data surface's knowledge-base picker pages through the same list with
  // a cursor, which is a different cache shape than the flat list the
  // KnowledgeBase pages read — hence its own key under the shared prefix.
  knowledgeBasePicker: (projectId: string | null) =>
    ["knowledgeBases", { project: projectId }, "picker"] as const,
  documents: (knowledgeBaseId: string) => ["knowledgeBases", knowledgeBaseId, "documents"] as const,
  connectors: (projectId: string | null) =>
    ["connectors", { project: projectId }] as const,
  // Bucket and table listings live under the same "connectors" prefix so one
  // prefix invalidation refreshes the connector list and any cached browses.
  connectorObjects: (connectorId: string) =>
    ["connectors", connectorId, "objects"] as const,
  connectorTables: (connectorId: string) =>
    ["connectors", connectorId, "tables"] as const,
  // The multi-select resource-browse panel (design 1n) keys by prefix so each
  // folder of an object store caches separately as the breadcrumb navigates;
  // SQL sources pass `null` (their schema is the connector's fixed boundary).
  // Under the "connectors" prefix so a connector invalidation clears browses.
  connectorResources: (connectorId: string, prefix: string | null = null) =>
    ["connectors", connectorId, "resources", { prefix }] as const,
  // (W8) The recurring sync schedule belongs to a dataset source, so it is read
  // with the dataset's sources and mutated through the
  // /datasets/{id}/sources/{sourceId}/schedule endpoints — no separate query
  // key: a schedule change invalidates the "datasets" prefix.
  // Tenancy control plane (RU-4): the caller (/me), the organizations they
  // belong to, and each org's members and invitations. Members and invitations
  // key UNDER the owning org's "organizations" prefix so one prefix
  // invalidation (`invalidateQueries({ queryKey: ["organizations"] })`)
  // refreshes an org, its roster, and its pending invites together; the org
  // entity uses `["organizations", id]`. A single invitation opened by id (the
  // accept-invitation surface) is a distinct resource and keys under its own
  // "invitation" prefix.
  // Identity-backed reads include the signed-in user as well as the tenant.
  // TanStack keeps its cache across an in-app sign-out/sign-in, so an org id
  // alone is not enough to prevent the previous account's rows flashing in a
  // new account before its request settles.
  me: (userId: string | null) => ["me", { user: userId }] as const,
  organizations: (userId: string | null) =>
    ["organizations", { user: userId }] as const,
  organization: (userId: string | null, id: string | null) =>
    ["organizations", { user: userId }, id] as const,
  orgMembers: (userId: string | null, organizationId: string | null) =>
    ["organizations", { user: userId }, organizationId, "members"] as const,
  orgInvitations: (userId: string | null, organizationId: string | null) =>
    ["organizations", { user: userId }, organizationId, "invitations"] as const,
  authSessions: (userId: string | null) =>
    ["auth-sessions", { user: userId }] as const,
  invitation: (id: string) => ["invitation", id] as const,
  apiKeys: () => ["api-keys"] as const,
  // A signed-in user's active-organization notification feed. Both tenancy
  // dimensions are part of the key: changing either identity must never expose
  // or mutate another session scope's cached rows. Nulls back the disabled
  // query while the live session is still resolving.
  notifications: (userId: string | null, organizationId: string | null) =>
    ["notifications", { user: userId, organization: organizationId }] as const,
  notificationPreferences: () => ["notification-preferences"] as const,
  // The usage summary, keyed by period ("YYYY-MM"); null = the live current
  // period. Every variant caches separately so a period picker can browse
  // settled months, while a bare `["usage"]` prefix invalidation still refreshes
  // them all. `qk.usage()` (period null) is the current period. GET /usage/summary
  // now accepts `?period=` (RU-5), so a non-null period re-keys the query to that
  // settled month; the closed-month browse in UsagePeriodPicker is live (PT-2 #1).
  usage: (period: string | null = null) => ["usage", { period }] as const,
  // Per-dimension usage breakdown (GET /usage/breakdown?groupBy=), the reshaped
  // home of the by-project / by-API-key / by-deployment figures that used to ride
  // inside the summary (RU-5). Keyed by dimension + period under the shared
  // "usage" prefix so a bare `["usage"]` invalidation refreshes the summary and
  // every breakdown together after a save or a settle.
  usageBreakdown: (dimension: string, period: string | null = null) =>
    ["usage", "breakdown", { dimension, period }] as const,
  billing: () => ["billing"] as const,
  // Lives under the "billing" prefix so invalidating qk.billing() also
  // refreshes the invoice list.
  billingInvoices: () => ["billing", "invoices"] as const,
  // The account's effective enforced limits (GET /billing/limits, PT-2) — under
  // the "billing" prefix so a billing invalidation refreshes them alongside the
  // balance and invoices.
  limits: () => ["billing", "limits"] as const,
  // The audit-event feed (GET /audit-events), keyed by its who/what/when
  // filters — action (what), actorUserId (who), and the `since` lower bound
  // (when) — so each filter combination caches separately while the shared
  // "audit" prefix keeps one invalidation refreshing them all.
  audit: (
    filter: { action?: string | null; actorUserId?: string | null; since?: string | null } = {},
  ) =>
    [
      "audit",
      {
        action: filter.action ?? null,
        actorUserId: filter.actorUserId ?? null,
        since: filter.since ?? null,
      },
    ] as const,
  // Distinct from `audit`: that key backs an infinite query and the two
  // cache shapes must not collide, but the shared "audit" prefix keeps one
  // invalidation refreshing both.
  auditRecent: () => ["audit", "recent"] as const,
  // Data objects (W4-UI-01): one project-scoped source identity whose
  // revisions are a separate read. Everything keys under the one "dataObjects"
  // prefix, so a single invalidation refreshes the list, an object and every
  // fact hanging off its revisions.
  dataObjects: (
    projectId: string | null,
    filters: { q?: string | null; state?: string | null; batch?: string | null } = {},
  ) => ["dataObjects", { project: projectId, ...filters }] as const,
  dataObject: (id: string) => ["dataObjects", id] as const,
  // Knowledge Base projections currently using one Data object. The relation
  // remains under the object's cache prefix, while every affected Knowledge
  // Base view stays under the separate "knowledgeBases" prefix.
  dataObjectKnowledgeBases: (id: string) =>
    ["dataObjects", id, "knowledgeBases"] as const,
  dataObjectRevisions: (id: string) => ["dataObjects", id, "revisions"] as const,
  // The paged history is a different cache shape than the single newest
  // revision the derived state reads, so it keys separately under the same
  // prefix rather than colliding with it.
  dataObjectRevisionHistory: (id: string) => ["dataObjects", id, "revision-history"] as const,
};
